import { getFilteredNavigationConfig } from '@/constants/navigation';
import { frontEndRoutes } from '@/constants/routes';
import { getUserFeatures } from '@/services/firebaseServices/AuthServices/helpers';
import type { UserClaims } from '@/services/firebaseServices/AuthServices/types';
import type { FirebaseAccountLinkingError } from '@/types/auth';
import { NavigationSection } from '@/types/navigation';

import { getJWTCookieClient } from './firebase-helpers';

/**
 * Decode JWT token payload to extract claims
 * JWT format: header.payload.signature (base64url encoded)
 *
 * @param token - JWT token string
 * @returns Decoded claims or null if decoding fails
 */
export function decodeJWTClaims(token: string): UserClaims | null {
  try {
    const parts = token.split('.');
    const payload = parts[1];
    if (parts.length !== 3 || payload === undefined) {
      return null;
    }

    // Base64url decode: replace URL-safe chars and add padding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(paddedBase64);
    const claims = JSON.parse(decoded) as UserClaims;

    return claims;
  } catch {
    return null;
  }
}

/**
 * Get user claims from JWT cookie (client-side only)
 * Use this as fallback when Firebase Auth is unavailable
 *
 * @returns UserClaims from JWT cookie or null if not available/invalid
 */
export function getClaimsFromJWTCookie(): UserClaims | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const jwtToken = getJWTCookieClient();
  if (jwtToken === '') {
    return null;
  }

  return decodeJWTClaims(jwtToken);
}

/**
 * Helper function to get user display name with fallbacks
 */
export function getUserDisplayName(user: { displayName?: string | null; email?: string | null } | null): string {
  if (user === null) {
    return 'User';
  }

  if (user.displayName !== null && user.displayName !== undefined && user.displayName !== '') {
    return user.displayName;
  }

  if (user.email !== null && user.email !== undefined) {
    const emailParts = user.email.split('@');
    const username = emailParts[0];
    return username !== undefined ? username : 'User';
  }

  return 'User';
}

/**
 * Unified authentication state checker for client-side
 * Checks authentication in priority order (aligned with getAuthToken):
 * 1. Firebase auth state (primary check)
 * 2. JWT cookie (secondary check)
 *
 * @param firebaseUser - Firebase user object or null
 * @returns true if user is authenticated via Firebase auth OR JWT
 */
export function isUserAuthenticated(firebaseUser: { uid: string } | null): boolean {
  // Priority 1: Check Firebase auth state
  if (firebaseUser !== null && firebaseUser.uid !== undefined && firebaseUser.uid !== '') {
    return true;
  }

  // Priority 2: Check JWT cookie
  const jwtToken = getJWTCookieClient();
  if (jwtToken !== null && jwtToken !== undefined && jwtToken !== '') {
    return true;
  }

  return false;
}

/**
 * Checks if JWT cookie exists (client-side)
 * @returns true if JWT cookie is set and non-empty
 */
export function hasValidJWT(): boolean {
  const jwtToken = getJWTCookieClient();
  return jwtToken !== null && jwtToken !== undefined && jwtToken !== '';
}

/**
 * Checks if Firebase user is authenticated
 * @param firebaseUser - Firebase user object or null
 * @returns true if Firebase user exists and has valid UID
 */
export function hasValidFirebaseUser(firebaseUser: { uid: string } | null): boolean {
  return firebaseUser !== null && firebaseUser.uid !== undefined && firebaseUser.uid !== '';
}

/**
 * Extract the first available space from JWT claims
 * Returns the first space ID found in the coaches object
 */
export function extractSpaceFromClaims(claims: UserClaims): string | null {
  if (claims.coaches === undefined || claims.coaches === null) {
    return null;
  }

  const spaces = Object.keys(claims.coaches);
  if (spaces.length === 0) {
    return null;
  }

  return spaces[0] ?? null;
}

/**
 * Type guard to check if an error is a Firebase account linking error
 * Firebase throws 'auth/account-exists-with-different-credential' when
 * a user tries to sign in with a provider that uses an email already
 * associated with a different account
 *
 * @param error - Unknown error object to check
 * @returns True if error is a Firebase account linking error with required properties
 */
export function isFirebaseAccountLinkingError(error: unknown): error is FirebaseAccountLinkingError {
  if (error === null || error === undefined) {
    return false;
  }

  if (typeof error !== 'object') {
    return false;
  }

  if ('code' in error === false || typeof error.code !== 'string') {
    return false;
  }

  if (error.code !== 'auth/account-exists-with-different-credential') {
    return false;
  }

  if ('email' in error === false || typeof error.email !== 'string' || error.email === '') {
    return false;
  }

  if ('credential' in error === false || error.credential === null || error.credential === undefined) {
    return false;
  }

  return true;
}

/**
 * Helper function to get the SSO Tenant ID for a given email. Undefined if the email is not supported.
 */
export function ssoTenantIDForEmail(email?: string): string | undefined {
  const emailEnding = email?.split('@')[1];

  // Only supporting DHL atm.
  if (
    emailEnding === 'mydpdhl.com'
    || emailEnding === 'dhl.com'
    || emailEnding === 'deutschepost.de'
  ) {
    return 'cd99fef8-1cd3-4a2a-9bdf-15531181d65e';
  }

  return undefined;
}

/**
 * Get the first accessible route for a user based on their claims and space
 * Used client-side after sign-in to redirect to the appropriate page
 *
 * @param claims - User claims from JWT token
 * @param spaceId - Active space ID
 * @returns First accessible route URL or notAllowed route if no access
 */
export function getFirstAccessibleRouteClient(claims: UserClaims, spaceId: string): string {
  const features = getUserFeatures(claims, spaceId);
  const filteredNavConfig = getFilteredNavigationConfig(spaceId, features);

  const allAccessibleItems = [
    ...filteredNavConfig[NavigationSection.TRAINING],
    ...filteredNavConfig[NavigationSection.CONTENT],
    ...filteredNavConfig[NavigationSection.ADMIN],
  ];

  const firstItem = allAccessibleItems[0];
  return firstItem?.url ?? frontEndRoutes.notAllowed;
}
